/**
 * What the Color Converter shows for one colour: every notation as a string, the CSS
 * keyword that names it (or the nearest one), and a block of CSS custom properties.
 */

import { CSS_COLOR_NAMES } from "@/lib/tool-kit/color-charts/color-names";
import {
  oklabDistance,
  type Rgb,
  toCmykString,
  toHex,
  toHex8,
  toHslString,
  toHsvString,
  toHwbString,
  toOklchString,
  toRgbString,
} from "@/lib/tool-kit/color-charts/color-space";

export type FormatKey =
  | "hex"
  | "hex8"
  | "rgb"
  | "hsl"
  | "hwb"
  | "hsv"
  | "cmyk"
  | "oklch";

export interface FormatRow {
  key: FormatKey;
  value: string;
}

/** The notations to list. `hex8` appears only when the colour is not opaque. */
export function formatRows(rgb: Rgb): FormatRow[] {
  const rows: FormatRow[] = [
    { key: "hex", value: toHex(rgb) },
    { key: "rgb", value: toRgbString(rgb) },
    { key: "hsl", value: toHslString(rgb) },
    { key: "hwb", value: toHwbString(rgb) },
    { key: "hsv", value: toHsvString(rgb) },
    { key: "cmyk", value: toCmykString(rgb) },
    { key: "oklch", value: toOklchString(rgb) },
  ];
  if (rgb.a < 1) {
    rows.splice(1, 0, { key: "hex8", value: toHex8(rgb) });
  }
  return rows;
}

export interface NameMatch {
  name: string;
  /** True when the 8-bit colour is exactly the keyword's colour. */
  exact: boolean;
}

/** The CSS keyword closest to `rgb` in OKLab, ignoring alpha. */
export function nearestName(rgb: Rgb): NameMatch {
  const opaque = { ...rgb, a: 1 };
  const hex = toHex(opaque);
  let best = CSS_COLOR_NAMES[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const entry of CSS_COLOR_NAMES) {
    if (entry[1] === hex) {
      return { name: entry[0], exact: true };
    }
    const byte = (at: number) =>
      Number.parseInt(entry[1].slice(at, at + 2), 16);
    const distance = oklabDistance(opaque, {
      r: byte(1),
      g: byte(3),
      b: byte(5),
      a: 1,
    });
    if (distance < bestDistance) {
      best = entry;
      bestDistance = distance;
    }
  }
  return { name: best[0], exact: false };
}

/** A paste-ready block of custom properties. */
export function cssBlock(rgb: Rgb): string {
  const lines = formatRows(rgb)
    .filter(
      (row) =>
        row.key === "hex" ||
        row.key === "hex8" ||
        row.key === "rgb" ||
        row.key === "hsl" ||
        row.key === "oklch"
    )
    .map((row) => `  --color-${row.key}: ${row.value};`);
  return `:root {\n${lines.join("\n")}\n}`;
}
